/**
 * Centralized field-size limits (single source of truth).
 *
 * These are the Syscolect-wide defaults for common text fields. Declare them on
 * the entity descriptors (e.g. `maxLength: FIELD_LIMITS.NAME`) so the same number
 * drives both the UI input cap and the value-validation engine — never hardcode
 * `15`/`150` in a component or a validator.
 *
 * Exceptions are allowed: a descriptor may override with its own number when a
 * field genuinely needs a different limit.
 */
export const FIELD_LIMITS = {
    /** Default max length for human-readable "name" fields. */
    NAME: 150,
    /** Default max length for short "abbreviation" fields. */
    ABBREVIATION: 15,
    /** Max length for organization email domain / company URL fields. */
    EMAIL_DOMAIN: 253,
    /** Default max length for "code"/"reference" fields. */
    CODE: 30,
    /** Max length for product barcode / GTIN fields (EAN-13, UPC, Code 128, etc.). */
    BARCODE: 48,
    /** NCM (Nomenclatura Comum do Mercosul) — 8 digits when present (ref.ncm lookup — 0396). */
    NCM: 8,
    /** Default max length for unit-of-measure fields. */
    UNIT: 15,
    /** NFe access key (chave de acesso) — exactly 44 digits when present. */
    NFE_ACCESS_KEY: 44,
    /** CNPJ length (14) — numeric or alphanumeric (IN RFB 2.229/2024). */
    CNPJ: 14,
    /** SHA-256 hex digest (64 lowercase hex chars). */
    SHA256: 64,
    /** ExternalRef.systemKey (e.g. 'vh:data-product'). */
    EXTERNAL_SYSTEM_KEY: 80,
    /** ExternalRef.entityName (e.g. 'Product'). */
    EXTERNAL_ENTITY_NAME: 80,
    /** ExternalRef.externalId — keep generous for legacy numeric/string ids. */
    EXTERNAL_ID: 200,
    /** ExternalRef.localId (UUID / document id). */
    EXTERNAL_LOCAL_ID: 80,
    /** Supplier product code (cProd) — keep generous; zeros preserved as string. */
    SUPPLIER_PRODUCT_CODE: 60,
    /** Long free-text notes on transactional documents (e.g. invoice). */
    NOTES: 2000,
    /** OS printer name as reported by the VectorHub agent. */
    PRINTER_OS_NAME: 256,
    /** User-friendly printer label (SC registry / VH agent config). */
    PRINTER_LABEL: 120,
    /** Optional printer description in the SC registry. */
    PRINTER_DESCRIPTION: 500,
    /** Workspace/stage/rule description fields. Also caps processInstructions
     * (route / override process / production-order step). */
    DESCRIPTION: 500,
    /** Ticket title — reuses NAME cap. */
    TITLE: 150,
    /** URL-safe key fields — reuses CODE cap. */
    KEY: 30,
    /** Main ticket request body. */
    TICKET_DETAILS: 5000,
    /** Markdown working-notes area on tickets. */
    TICKET_MARKDOWN: 20000,
    /** Message thread body. */
    MESSAGE_BODY: 5000,
    /** Human-readable lifecycle status explanation. */
    STATUS_MESSAGE: 500,
    /** Attachment file name. */
    FILE_NAME: 255,
    /** MIME type string. */
    MIME_TYPE: 100,
    /** Cloud storage path reference. */
    STORAGE_PATH: 500,
    /** Board column color (hex or CSS name). */
    COLOR: 30,
    /** Automation rule script source. */
    SCRIPT_SOURCE: 20000,
    /** Display name on ticket participants/messages — reuses NAME cap. */
    USER_NAME: 150,
    /** Operator note / cancel reason on warehouse tasks (0166 §6). */
    OPERATOR_NOTE: 500,
    /** StorageAddressPattern.mask — tokens + literals, e.g. R{rua}-P{predio}-N{nivel}-A{posicao}. */
    STORAGE_ADDRESS_MASK: 120,
    /** UF / state abbreviation — exactly 2 letters. */
    UF: 2,
    /** IBGE state code (cUF) — 2 digits. */
    IBGE_STATE_CODE: 2,
    /** IBGE municipality code (cMun) — 7 digits. */
    IBGE_CITY_CODE: 7,
    /** Brazilian postal code (CEP) — 8 digits, stored unmasked. */
    ZIP_CODE: 8,
    /** Street number of a postal address — string, not number ("123-A", "s/n", "km 42"). */
    ADDRESS_NUMBER: 20,
    /** Address complement (apartment, block, room, warehouse bay). */
    ADDRESS_COMPLEMENT: 100,
    /** Address district (bairro). */
    ADDRESS_DISTRICT: 100,
    /** SavedSelection.screen — PaginatedCrudPage `name` key. */
    SAVED_SELECTION_SCREEN: 120,
    /** Cap on `JSON.stringify(SavedSelection.layout)` (opaque DynamicQueryLayout v2). */
    SAVED_SELECTION_LAYOUT_JSON: 32768,
};
/**
 * Max allowed value for quantity-style numeric fields (e.g. textile metrics:
 * grammage, widths). Equals 999.999.999.999,999 (12 integer digits + 3 decimals).
 * Quantities must also be non-negative (min 0).
 */
export const MAX_QUANTITY = 999999999999.999;
/**
 * Máximo de casas decimais aceitas numa quantidade (spec 0032 §3).
 * 3 cobre kg/litro/metro; 4 cobre consumo têxtil e químico por unidade produzida
 * (g/peça, ml/litro). Acima disso é medida de laboratório, não de estoque.
 */
export const MAX_QUANTITY_DECIMALS = 4;
/**
 * Max for numeric integer codes on smaller master-data entities
 * (e.g. sector, color when/if stored as number). Equals 999.999.
 * Distinct from FIELD_LIMITS.CODE (string maxLength for text codes/references).
 */
export const MAX_CODE_SMALL = 999999;
/**
 * Max for numeric integer codes on large catalogs (e.g. product). Equals 999.999.999.
 */
export const MAX_CODE_LARGE = 999999999;
/**
 * Max allowed value for duration fields stored in milliseconds (ADR decisions/0001).
 * Capped at 7 days — comfortably above any single operation or setup time.
 * Duration fields must also be non-negative (min 0).
 */
export const MAX_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 604_800_000 ms
/**
 * Cap for a single WorkShift / working-exception duration (spec 0042 §2.2).
 * One line is at most 24h; a second interval is a second row.
 */
export const MAX_WORK_SHIFT_DURATION_MS = 24 * 60 * 60 * 1000; // 86_400_000 ms
/** Last valid wall-clock minute of day (23:59). Used by `startMinuteOfDay` / `dayStartMinuteOfDay`. */
export const MAX_MINUTE_OF_DAY = 1439;
/** Max for quoted/approved effort fields stored in ms (ticket quotes). ≈ 9 999 h. */
export const MAX_EFFORT_MS = 9999 * 60 * 60 * 1000;
/** Max number of VectorHub agents an org may register (UI: min 1, max VH_MAX_AGENTS_MAX). */
export const VH_MAX_AGENTS_MAX = 999;
/** Sensible cap for board column order index. */
export const MAX_STAGE_ORDER_INDEX = 999;
/** Cap sensato para a ordem de exibição de um tamanho na grade. */
export const MAX_SIZE_ORDER = 999;
/** Default automation script timeout when not specified on the rule (ms). */
export const DEFAULT_AUTOMATION_TIMEOUT_MS = 5000;
/** Max automation script timeout (ms). */
export const MAX_AUTOMATION_TIMEOUT_MS = 60000;
/** Max attachment file size (bytes). */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
/** Max attachments per ticket. */
export const MAX_TICKET_ATTACHMENTS = 20;
/** Blocked executable-ish extensions (lowercase, without dot). */
export const BLOCKED_ATTACHMENT_EXTENSIONS = [
    'exe',
    'bat',
    'cmd',
    'com',
    'msi',
    'scr',
    'ps1',
    'sh',
    'js',
    'jar',
    'vbs',
];
//# sourceMappingURL=fieldLimits.js.map